import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Download,
  Eye,
  ExternalLink,
  Search,
  Filter,
  Grid3X3,
  List,
  Calendar,
  User,
  File,
  Image,
  Archive,
  FileSpreadsheet,
  FileCode,
  ChevronDown,
  ChevronUp,
  SortAsc,
  SortDesc,
  RefreshCw,
  Info,
  AlertCircle,
  Key,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { DocumentModal } from "../../../components/ui/DocumentModal";
import { getFilesByIPRId } from "../../../../ApiCalls/mantisFlowsManangment";
import { useToast } from "../../../components/ui/use-toast";

const DocumentsSection = ({ clientId, iprId, currentUser }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterBy, setFilterBy] = useState("all");
  const [documentModal, setDocumentModal] = useState({
    isOpen: false,
    documents: [],
    item: null,
  });
  const [expandedFile, setExpandedFile] = useState(null);
  const { toast } = useToast();

  // File type icons mapping based on mime_type
  const getFileIcon = (mimeType, filename) => {
    if (mimeType?.includes("pdf")) {
      return <FileText className="h-5 w-5 text-red-500" />;
    } else if (
      mimeType?.includes("word") ||
      mimeType?.includes("document") ||
      filename?.endsWith(".doc") ||
      filename?.endsWith(".docx")
    ) {
      return <FileText className="h-5 w-5 text-blue-600" />;
    } else if (
      mimeType?.includes("excel") ||
      mimeType?.includes("spreadsheet") ||
      filename?.endsWith(".xls") ||
      filename?.endsWith(".xlsx")
    ) {
      return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
    } else if (mimeType?.includes("image")) {
      return <Image className="h-5 w-5 text-purple-500" />;
    } else if (
      mimeType?.includes("zip") ||
      mimeType?.includes("rar") ||
      filename?.endsWith(".zip") ||
      filename?.endsWith(".rar") ||
      filename?.endsWith(".7z")
    ) {
      return <Archive className="h-5 w-5 text-orange-500" />;
    } else if (
      mimeType?.includes("text") ||
      filename?.endsWith(".txt") ||
      filename?.endsWith(".md")
    ) {
      return <FileCode className="h-5 w-5 text-gray-500" />;
    } else {
      return <File className="h-5 w-5 text-gray-400" />;
    }
  };

  // Get file type category based on mime_type
  const getFileCategory = (mimeType, filename) => {
    if (mimeType?.includes("pdf")) return "documents";
    if (
      mimeType?.includes("word") ||
      mimeType?.includes("document") ||
      filename?.endsWith(".doc") ||
      filename?.endsWith(".docx")
    )
      return "documents";
    if (
      mimeType?.includes("excel") ||
      mimeType?.includes("spreadsheet") ||
      filename?.endsWith(".xls") ||
      filename?.endsWith(".xlsx")
    )
      return "spreadsheets";
    if (mimeType?.includes("image")) return "images";
    if (
      mimeType?.includes("zip") ||
      mimeType?.includes("rar") ||
      filename?.endsWith(".zip") ||
      filename?.endsWith(".rar") ||
      filename?.endsWith(".7z")
    )
      return "archives";
    return "other";
  };

  // Format file size from size_in_bytes
  const formatFileSize = (sizeInBytes) => {
    if (!sizeInBytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(k));
    return (
      parseFloat((sizeInBytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
    );
  };

  // Format date from created_at
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Load files from API
  const loadFiles = useCallback(async () => {
    if (!clientId || !iprId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getFilesByIPRId(clientId, iprId);
      setFiles(response.data || []);
    } catch (err) {
      console.error("Error loading files:", err);
      setError("Error al cargar los archivos. Por favor, inténtalo de nuevo.");
      toast({
        title: "Error",
        description: "No se pudieron cargar los archivos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [clientId, iprId, toast]);

  // Load files on component mount
  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Filter and sort files based on the actual file object structure
  const filteredAndSortedFiles = files
    .filter((file) => {
      const matchesSearch =
        file.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.key?.toLowerCase().includes(searchTerm.toLowerCase());

      if (filterBy === "all") return matchesSearch;
      return (
        matchesSearch &&
        getFileCategory(file.mime_type, file.filename) === filterBy
      );
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = (a.filename || "").toLowerCase();
          bValue = (b.filename || "").toLowerCase();
          break;
        case "size":
          aValue = a.size_in_bytes || 0;
          bValue = b.size_in_bytes || 0;
          break;
        case "date":
        default:
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Handle file selection
  const toggleFileSelection = (fileId) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  // Handle bulk selection
  const selectAllFiles = () => {
    setSelectedFiles(filteredAndSortedFiles.map((file) => file.id));
  };

  const clearSelection = () => {
    setSelectedFiles([]);
  };

  // Handle file download
  const handleDownload = (file) => {
    const link = document.createElement("a");
    link.href = file.url;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Descarga iniciada",
      description: `Descargando ${file.filename}`,
    });
  };

  // Handle file preview
  const handlePreview = (file) => {
    setDocumentModal({
      isOpen: true,
      documents: [
        {
          fileUrl: file.url,
          filename: file.filename,
        },
      ],
      item: file,
    });
  };

  // Handle bulk download
  const handleBulkDownload = () => {
    selectedFiles.forEach((fileId) => {
      const file = files.find((f) => f.id === fileId);
      if (file) {
        handleDownload(file);
      }
    });

    toast({
      title: "Descarga masiva",
      description: `Descargando ${selectedFiles.length} archivos`,
    });
  };

  // File card component
  const FileCard = ({ file }) => {
    const isSelected = selectedFiles.includes(file.id);
    const isExpanded = expandedFile === file.id;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`
          relative bg-white dark:bg-slate-800 rounded-lg border-2 transition-all duration-200
          ${
            isSelected
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
              : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
          }
        `}
      >
        {/* Selection checkbox */}
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleFileSelection(file.id)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
        </div>

        <div className="p-4">
          {/* File header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <div className="flex-shrink-0">
                {getFileIcon(file.mime_type, file.filename)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                  {file.filename}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {formatFileSize(file.size_in_bytes)}
                </p>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpandedFile(isExpanded ? null : file.id)}
              className="h-6 w-6 p-0"
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* File actions */}
          <div className="flex items-center justify-between">
            <div className="flex space-x-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePreview(file)}
                className="h-7 px-2 text-xs"
              >
                <Eye className="h-3 w-3 mr-1" />
                Ver
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownload(file)}
                className="h-7 px-2 text-xs"
              >
                <Download className="h-3 w-3 mr-1" />
                Descargar
              </Button>
            </div>
          </div>

          {/* Expanded details */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700"
              >
                <div className="space-y-2 text-xs text-slate-600 dark:text-slate-400">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-3 w-3" />
                    <span>Creado: {formatDate(file.created_at)}</span>
                  </div>
                  {file.uploaded_k && (
                    <div className="flex items-center space-x-2">
                      <User className="h-3 w-3" />
                      <span>Subido por: {file.uploaded_k}</span>
                    </div>
                  )}
                  {file.key && (
                    <div className="flex items-center space-x-2">
                      <Key className="h-3 w-3" />
                      <span>Clave: {file.key}</span>
                    </div>
                  )}
                  {file.mime_type && (
                    <div className="flex items-center space-x-2">
                      <Info className="h-3 w-3" />
                      <span>Tipo: {file.mime_type}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    );
  };

  // File list item component
  const FileListItem = ({ file }) => {
    const isSelected = selectedFiles.includes(file.id);

    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className={`
          flex items-center space-x-4 p-3 rounded-lg border transition-all duration-200
          ${
            isSelected
              ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700"
              : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
          }
        `}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleFileSelection(file.id)}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
        />

        <div className="flex-shrink-0">
          {getFileIcon(file.mime_type, file.filename)}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
            {file.filename}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {formatFileSize(file.size_in_bytes)} • {formatDate(file.created_at)}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Badge variant="outline" className="text-xs">
            {getFileCategory(file.mime_type, file.filename)}
          </Badge>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePreview(file)}
            className="h-7 px-2 text-xs"
          >
            <Eye className="h-3 w-3 mr-1" />
            Ver
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownload(file)}
            className="h-7 px-2 text-xs"
          >
            <Download className="h-3 w-3 mr-1" />
            Descargar
          </Button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Documentos del IPR
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Gestiona y visualiza todos los archivos relacionados con esta
            solicitud
          </p>
        </div>

        <Button
          onClick={loadFiles}
          disabled={loading}
          variant="outline"
          className="flex items-center space-x-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          <span>Actualizar</span>
        </Button>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search and filters */}
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar archivos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterBy} onValueChange={setFilterBy}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="documents">Documentos</SelectItem>
              <SelectItem value="spreadsheets">Hojas de cálculo</SelectItem>
              <SelectItem value="images">Imágenes</SelectItem>
              <SelectItem value="archives">Archivos comprimidos</SelectItem>
              <SelectItem value="other">Otros</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Fecha</SelectItem>
              <SelectItem value="name">Nombre</SelectItem>
              <SelectItem value="size">Tamaño</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="px-2"
          >
            {sortOrder === "asc" ? (
              <SortAsc className="h-4 w-4" />
            ) : (
              <SortDesc className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* View mode and bulk actions */}
        <div className="flex items-center space-x-2">
          <div className="flex border border-slate-200 dark:border-slate-700 rounded-lg">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className={`rounded-r-none ${
                viewMode === "grid"
                  ? "bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
                  : "hover:bg-orange-50 dark:hover:bg-orange-900/20"
              }`}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className={`rounded-l-none ${
                viewMode === "list"
                  ? "bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
                  : "hover:bg-orange-50 dark:hover:bg-orange-900/20"
              }`}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          {selectedFiles.length > 0 && (
            <div className="flex items-center space-x-2">
              <Badge variant="secondary">
                {selectedFiles.length} seleccionado
                {selectedFiles.length !== 1 ? "s" : ""}
              </Badge>
              <Button variant="outline" size="sm" onClick={handleBulkDownload}>
                <Download className="h-4 w-4 mr-1" />
                Descargar
              </Button>
              <Button variant="ghost" size="sm" onClick={clearSelection}>
                Limpiar
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Bulk selection controls */}
      {filteredAndSortedFiles.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center space-x-4">
            <Button variant="outline" size="sm" onClick={selectAllFiles}>
              Seleccionar todos
            </Button>
            <Button variant="outline" size="sm" onClick={clearSelection}>
              Limpiar selección
            </Button>
          </div>

          <div className="text-sm text-slate-600 dark:text-slate-400">
            {filteredAndSortedFiles.length} archivo
            {filteredAndSortedFiles.length !== 1 ? "s" : ""} encontrado
            {filteredAndSortedFiles.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="text-slate-600 dark:text-slate-400">
              Cargando archivos...
            </span>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Error al cargar archivos
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
            <Button onClick={loadFiles} variant="outline">
              Intentar de nuevo
            </Button>
          </div>
        </div>
      ) : filteredAndSortedFiles.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              No hay archivos
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              {searchTerm || filterBy !== "all"
                ? "No se encontraron archivos con los filtros aplicados"
                : "No hay archivos disponibles para esta solicitud"}
            </p>
          </div>
        </div>
      ) : (
        <div
          className={
            viewMode === "grid"
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              : "space-y-2"
          }
        >
          <AnimatePresence>
            {filteredAndSortedFiles.map((file) => (
              <motion.div
                key={file.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
              >
                {viewMode === "grid" ? (
                  <FileCard file={file} />
                ) : (
                  <FileListItem file={file} />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Document Modal */}
      <DocumentModal
        isOpen={documentModal.isOpen}
        onClose={() =>
          setDocumentModal({ isOpen: false, documents: [], item: null })
        }
        documents={documentModal.documents}
        item={documentModal.item}
        onDownload={handleDownload}
      />
    </div>
  );
};

export default DocumentsSection;
